/**
 * +capture: deterministic frame capture for the trailer (dev tooling; only with `?capture=1`).
 *
 * Imported first by main.ts; without `?capture=1` it does nothing. With it, the page runs on a
 * virtual clock: performance.now / Date.now / requestAnimationFrame / setTimeout / setInterval and
 * the page's CSS animations all advance only when the clock is pumped. While "free" (booting,
 * loading) a real rAF pumps it with wall time; `__capture.freeze()` stops that and
 * `__capture.step(dtMs)` then advances sim + render + HUD by exactly dtMs, so a CDP script can
 * screenshot every frame of a smooth 30 fps video however slow each frame is to render.
 *
 *   __capture.freeze() / .resume()        stop / restart the wall-clock pump
 *   __capture.step(dtMs = 1000 / 30)      advance timers, rAF callbacks and CSS animations
 *   __capture.setCamera(pos, lookAt, fov) scripted camera, sim coords {x, y, h}; `h` is height
 *                                         above the ground at (x, y) unless `abs: true`
 *   __capture.clearCamera()               back to the game's chase camera
 *   __capture.hideHud(mode)               'all' | 'none' | ['.floaters', …] (hide all but these)
 *   __capture.caption(spec) / .clearCaptions()   fantasy-serif overlays timed on the virtual clock
 *   __capture.light(name)                 capture-only lighting preset ('golden', 'bright'; null = theme)
 *   __capture.presence                    a scripted stand-in for the presence shard (other heroes)
 *
 * The scripts that drive it live outside the repo (scratchpad/trailer/make.mjs).
 */
import { atmosphere } from '../render/toon/outdoor/atmosphere'; // +atmos
import { TOD_PRESETS, type TodPreset } from '../render/toon/outdoor/atmosphere/timeOfDay'; // +atmos
import type * as THREE from 'three';
import type { CameraRig } from '../render/camera';
import { type CaptureLights, captureHook } from './captureHook';
import { U } from '../render/toon/style';
import { OCC_U } from '../render/toon/outdoor/occlude';
import { simLocalState, startFieldPresence, stopFieldPresence } from '../net/fieldPresence';
import type { FieldEventOut, FieldPlayerInfo, FieldRow } from '../net/fieldProtocol';

type Fn = (...args: unknown[]) => void;
interface Timer {
  id: number;
  at: number;
  fn: Fn | string;
  args: unknown[];
  every: number | null;
}
interface CamSpec {
  pos: { x: number; y: number; h: number; abs?: boolean };
  look: { x: number; y: number; h: number; abs?: boolean };
  fov: number;
  roll?: number;
}
interface CaptionSpec {
  /** Big line. */
  title?: string;
  /** Small line under it. */
  sub?: string;
  /** 'center' (section cards), 'lower' (lower third), 'logo' (the KEYSTONE title card), 'url' (end card line). */
  kind?: 'center' | 'lower' | 'logo' | 'url';
  /** Virtual ms on screen (fades included). */
  dur?: number;
  fadeIn?: number;
  fadeOut?: number;
}

const params = new URLSearchParams(location.search);

// Dev builds (or ?debug) only, like the __keystone handle.
if (params.get('capture') === '1' && (import.meta.env.DEV || params.has('debug'))) install();

function install(): void {
  const realRaf = window.requestAnimationFrame.bind(window);
  const realSetTimeout = window.setTimeout.bind(window);
  const realNow = performance.now.bind(performance);
  const realDateNow = Date.now.bind(Date);
  const t0 = realNow();
  const d0 = realDateNow();
  let vnow = t0;
  let free = true;
  let lastReal = t0;

  // ---------------------------------------------------------------- clock + timers
  performance.now = () => vnow;
  Date.now = () => d0 + (vnow - t0);

  let nextId = 1;
  const timers = new Map<number, Timer>();
  const add = (fn: Fn | string, ms: unknown, args: unknown[], every: boolean): number => {
    const id = nextId++;
    const d = Math.max(0, Number(ms) || 0);
    timers.set(id, { id, at: vnow + d, fn, args, every: every ? Math.max(1, d) : null });
    return id;
  };
  const w = window as unknown as Record<string, unknown>;
  w['setTimeout'] = (fn: Fn | string, ms?: number, ...args: unknown[]) => add(fn, ms, args, false);
  w['setInterval'] = (fn: Fn | string, ms?: number, ...args: unknown[]) => add(fn, ms, args, true);
  w['clearTimeout'] = (id?: number) => void (id !== undefined && timers.delete(id));
  w['clearInterval'] = (id?: number) => void (id !== undefined && timers.delete(id));

  let rafs = new Map<number, FrameRequestCallback>();
  w['requestAnimationFrame'] = (cb: FrameRequestCallback) => {
    const id = nextId++;
    rafs.set(id, cb);
    return id;
  };
  w['cancelAnimationFrame'] = (id: number) => void rafs.delete(id);

  function runTimers(): void {
    for (let guard = 0; guard < 10_000; guard++) {
      let due: Timer | null = null;
      for (const t of timers.values()) if (t.at <= vnow && (!due || t.at < due.at || (t.at === due.at && t.id < due.id))) due = t;
      if (!due) return;
      if (due.every !== null) due.at += due.every;
      else timers.delete(due.id);
      try {
        if (typeof due.fn === 'function') due.fn(...due.args);
      } catch (err) {
        console.error('[capture] timer threw', err);
      }
    }
  }

  // CSS animations / transitions: held paused and moved by hand (finished ones are finished so
  // their end events fire and the HUD cleans them up).
  const seen = new WeakSet<Animation>();
  function stepAnimations(dt: number): void {
    for (const a of document.getAnimations()) {
      if (!seen.has(a)) {
        seen.add(a);
        if (a.playState === 'running') {
          a.pause();
          continue; // a fresh animation starts at 0 this frame
        }
      }
      if (a.playState !== 'paused') continue;
      const timing = a.effect?.getComputedTiming();
      const end = Number(timing?.endTime ?? Infinity);
      const cur = Number(a.currentTime ?? 0) + dt * (a.playbackRate || 1);
      if (Number.isFinite(end) && cur >= end) {
        try {
          a.finish();
        } catch {
          a.currentTime = end;
        }
      } else a.currentTime = cur;
    }
  }

  function pump(dt: number): void {
    vnow += dt;
    runTimers();
    const cbs = rafs;
    rafs = new Map();
    for (const cb of cbs.values()) {
      try {
        cb(vnow);
      } catch (err) {
        console.error('[capture] frame threw', err);
      }
    }
    stepAnimations(dt);
    tickOverlay();
  }

  function wallPump(): void {
    const r = realNow();
    const dt = Math.min(100, r - lastReal);
    if (dt < 8) return;
    lastReal = r;
    if (free) pump(dt);
  }
  const rafLoop = (): void => {
    wallPump();
    realRaf(rafLoop);
  };
  realRaf(rafLoop);
  // An occluded window may stop real frames: keep loading going on a plain timer as well.
  const timerLoop = (): void => {
    if (realNow() - lastReal > 120) wallPump();
    realSetTimeout(timerLoop, 50);
  };
  realSetTimeout(timerLoop, 50);

  // ---------------------------------------------------------------- lighting presets
  // Capture-only looks applied every frame over the theme's lighting (and put back when cleared):
  // the live game never reaches this code.
  interface Look {
    sun?: [number, number, number];
    key?: number;
    keyIntensity?: number;
    hemiSky?: number;
    hemiGround?: number;
    hemiIntensity?: number;
    fogNear?: number;
    fogFar?: number;
    fogColor?: [number, number, number];
    sat?: number;
    gain?: [number, number, number];
    lift?: [number, number, number];
    sky?: { top?: number; mid?: number; horizon?: number; cloud?: number; shade?: number };
  }
  const LOOKS: Record<string, Look> = {
    // Low warm late-afternoon sun from the west, long shadows, peach horizon, less haze.
    golden: {
      sun: [-0.9, 0.42, 0.18],
      key: 0xffc58a,
      keyIntensity: 3.6,
      hemiSky: 0xf2d9bd,
      hemiGround: 0x4b3b3a,
      hemiIntensity: 0.62,
      fogNear: 120,
      fogFar: 420,
      fogColor: [0.93, 0.8, 0.66],
      sat: 1.24,
      gain: [1.08, 1.0, 0.9],
      lift: [0.006, 0.004, 0.012],
      sky: { top: 0x3f6db8, mid: 0x8fb0d6, horizon: 0xffc796, cloud: 0xfff1de, shade: 0xb9909a },
    },
    // Bright midday for readability (nameplates, faces): neutral key, crisp, a touch warmer.
    bright: {
      keyIntensity: 3.6,
      hemiIntensity: 0.75,
      fogNear: 90,
      fogFar: 320,
      sat: 1.18,
      gain: [1.1, 1.06, 1.0],
    },
  };
  let look: Look | null = null;
  let saved: Record<string, unknown> | null = null;
  function applyLook(scene: THREE.Scene, l: CaptureLights): void {
    const sky = scene.getObjectByName('sky') as THREE.Mesh | undefined;
    const su = (sky?.material as THREE.ShaderMaterial | undefined)?.uniforms;
    if (!look) {
      if (saved) {
        const v = saved as Record<string, any>;
        l.keyDir.copy(v['keyDir']); l.key.color.copy(v['key']); l.key.intensity = v['keyI'];
        l.hemi.color.copy(v['hemiSky']); l.hemi.groundColor.copy(v['hemiGround']); l.hemi.intensity = v['hemiI'];
        U.uFogNear.value = v['fogNear']; U.uFogFar.value = v['fogFar']; U.uFogColor.value.copy(v['fogColor']);
        l.grade.uSat.value = v['sat']; l.grade.uGain.value.copy(v['gain']); l.grade.uLift.value.copy(v['lift']);
        U.uKeyIntensity.value = l.key.intensity / Math.PI;
        saved = null;
      }
      return;
    }
    if (!saved) saved = { keyDir: l.keyDir.clone(), key: l.key.color.clone(), keyI: l.key.intensity, hemiSky: l.hemi.color.clone(), hemiGround: l.hemi.groundColor.clone(), hemiI: l.hemi.intensity, fogNear: U.uFogNear.value, fogFar: U.uFogFar.value, fogColor: U.uFogColor.value.clone(), sat: l.grade.uSat.value, gain: l.grade.uGain.value.clone(), lift: l.grade.uLift.value.clone() };
    const L = look;
    if (L.sun) { l.keyDir.set(L.sun[0], L.sun[1], L.sun[2]).normalize(); su?.['uSun']?.value.copy(l.keyDir); }
    if (L.key !== undefined) l.key.color.setHex(L.key);
    if (L.keyIntensity !== undefined) l.key.intensity = L.keyIntensity;
    U.uKeyIntensity.value = l.key.intensity / Math.PI;
    if (L.hemiSky !== undefined) l.hemi.color.setHex(L.hemiSky);
    if (L.hemiGround !== undefined) l.hemi.groundColor.setHex(L.hemiGround);
    if (L.hemiIntensity !== undefined) l.hemi.intensity = L.hemiIntensity;
    if (L.fogNear !== undefined) U.uFogNear.value = L.fogNear;
    if (L.fogFar !== undefined) U.uFogFar.value = L.fogFar;
    if (L.fogColor) U.uFogColor.value.setRGB(L.fogColor[0], L.fogColor[1], L.fogColor[2]);
    if (L.sat !== undefined) l.grade.uSat.value = L.sat;
    if (L.gain) l.grade.uGain.value.set(L.gain[0], L.gain[1], L.gain[2]);
    if (L.lift) l.grade.uLift.value.set(L.lift[0], L.lift[1], L.lift[2]);
    if (L.sky && su) for (const [k, u] of [['top', 'uTop'], ['mid', 'uMid'], ['horizon', 'uHorizon'], ['cloud', 'uCloud'], ['shade', 'uShade']] as const) if (L.sky[k] !== undefined) su[u]?.value.setHex(L.sky[k]!);
  }

  // ---------------------------------------------------------------- camera
  let cam: CamSpec | null = null;
  let had = false;
  let labels = true;
  let labelsOff = false;
  const LABEL_LAYER = 7;
  captureHook.camera = (camera: THREE.PerspectiveCamera, rig: CameraRig, groundY: (x: number, y: number) => number, scene: THREE.Scene, lights: CaptureLights): number => {
    applyLook(scene, lights);
    // World labels (NPC names, quest markers: screen-sized sprites drawn over everything) can be
    // moved to a layer the camera doesn't draw.
    if (!labels || labelsOff) {
      scene.traverse((o) => {
        const m = (o as THREE.Sprite).isSprite ? ((o as THREE.Sprite).material as THREE.SpriteMaterial) : null;
        if (m && !m.sizeAttenuation && !m.depthTest) o.layers.set(labels ? 0 : LABEL_LAYER);
      });
      labelsOff = !labels;
    }
    if (!cam) {
      if (had) {
        had = false;
        camera.fov = rig.fov();
        camera.updateProjectionMatrix();
      }
      return camera.fov;
    }
    had = true;
    const p = cam.pos;
    const l = cam.look;
    const ph = p.abs ? p.h : groundY(p.x, p.y) + p.h;
    const lh = l.abs ? l.h : groundY(l.x, l.y) + l.h;
    camera.position.set(p.x, ph, -p.y);
    camera.up.set(0, 1, 0);
    camera.lookAt(l.x, lh, -l.y);
    if (cam.roll) camera.rotateZ(cam.roll);
    camera.fov = cam.fov;
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
    // Shadows, ambience and the effect LOD follow what the shot looks at.
    rig.state.focus = { x: l.x, y: l.y };
    return cam.fov;
  };

  // ---------------------------------------------------------------- HUD + captions
  const style = document.createElement('style');
  style.textContent = `
    html.cap-hud-all #hud, html.cap-hud-all .km-toasts { visibility: hidden !important; }
    html.cap-hud-keep #hud *, html.cap-hud-keep .km-toasts * { visibility: hidden !important; }
    html.cap-nocursor, html.cap-nocursor * { cursor: none !important; }
    .rp-plate { font-size: 24px !important; gap: 8px !important; }
    .rp-plate .rp-lvl { font-size: 17px !important; line-height: 22px !important; padding: 0 7px !important; border-radius: 10px !important; }
    #cap-overlay { position: fixed; inset: 0; pointer-events: none; z-index: 2147483000; font-family: 'Cinzel', 'Cormorant Garamond', Georgia, serif; color: #f6ecd2; }
    #cap-overlay .cap { position: absolute; left: 0; right: 0; text-align: center; opacity: 0; }
    #cap-overlay .cap.center { top: 20%; }
    #cap-overlay .cap.lower { bottom: 9%; left: 6%; right: auto; text-align: left; }
    #cap-overlay .cap.lower .rule { margin: 12px 0 0; background: linear-gradient(90deg, rgba(255,226,160,0.95), transparent); }
    #cap-overlay .cap.logo { top: 50%; transform: translateY(-58%); }
    #cap-overlay .cap.url { bottom: 14%; }
    #cap-overlay .url .t { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 34px; font-weight: 500; color: #ffe7b0; }
    #cap-overlay .url .rule { display: none; }
    #cap-overlay .t { font-weight: 600; text-shadow: 0 2px 18px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.9); white-space: nowrap; }
    #cap-overlay .center .t { font-size: 60px; }
    #cap-overlay .lower .t { font-size: 56px; }
    #cap-overlay .lower .s { font-size: 28px; margin-top: 8px; }
    #cap-overlay .logo .t { font-size: 150px; font-weight: 700; color: #ffe7b0; text-shadow: 0 4px 40px rgba(0,0,0,0.6), 0 0 30px rgba(255,190,90,0.45); }
    #cap-overlay .s { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 30px; margin-top: 10px; color: #efe3c4; text-shadow: 0 2px 10px rgba(0,0,0,0.85); letter-spacing: 0.06em; }
    #cap-overlay .logo .s { font-size: 34px; font-style: normal; letter-spacing: 0.3em; text-transform: uppercase; font-family: 'Cinzel', serif; }
    #cap-overlay .rule { width: 0; height: 1px; margin: 16px auto 0; background: linear-gradient(90deg, transparent, rgba(255,226,160,0.9), transparent); }
    #cap-overlay .shade { position: absolute; left: 0; right: 0; bottom: 0; height: 34%; background: linear-gradient(transparent, rgba(0,0,0,0.42)); opacity: 0; }
    #cap-overlay .black { position: absolute; inset: 0; background: #000; opacity: 0; }
  `;
  document.head.appendChild(style);
  const keepStyle = document.createElement('style');
  document.head.appendChild(keepStyle);
  if (params.get('fonts') !== '0') {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&family=Cormorant+Garamond:ital,wght@0,500;1,500&display=block';
    document.head.appendChild(link);
  }
  const overlay = document.createElement('div');
  overlay.id = 'cap-overlay';
  const shade = document.createElement('div');
  shade.className = 'shade';
  const black = document.createElement('div');
  black.className = 'black';
  overlay.append(shade, black);
  const mountOverlay = (): void => void document.body.appendChild(overlay);
  if (document.body) mountOverlay();
  else document.addEventListener('DOMContentLoaded', mountOverlay);

  interface Live {
    el: HTMLElement;
    t: HTMLElement | null;
    rule: HTMLElement;
    born: number;
    spec: Required<Pick<CaptionSpec, 'dur' | 'fadeIn' | 'fadeOut' | 'kind'>>;
  }
  let captions: Live[] = [];
  const ease = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

  function caption(spec: CaptionSpec): void {
    const kind = spec.kind ?? 'center';
    const el = document.createElement('div');
    el.className = `cap ${kind}`;
    let t: HTMLElement | null = null;
    if (spec.title) {
      t = document.createElement('div');
      t.className = 't';
      t.textContent = spec.title;
      el.appendChild(t);
    }
    const rule = document.createElement('div');
    rule.className = 'rule';
    el.appendChild(rule);
    if (spec.sub) {
      const s = document.createElement('div');
      s.className = 's';
      s.textContent = spec.sub;
      el.appendChild(s);
    }
    overlay.appendChild(el);
    captions.push({ el, t, rule, born: vnow, spec: { kind, dur: spec.dur ?? 3000, fadeIn: spec.fadeIn ?? 700, fadeOut: spec.fadeOut ?? 700 } });
    tickOverlay();
  }

  let shadeTarget = 0;
  let blackLevel = 0;
  function tickOverlay(): void {
    let lower = 0;
    captions = captions.filter((c) => {
      const age = vnow - c.born;
      if (age > c.spec.dur) {
        c.el.remove();
        return false;
      }
      const a = Math.min(ease(age / c.spec.fadeIn), ease((c.spec.dur - age) / c.spec.fadeOut));
      c.el.style.opacity = a.toFixed(3);
      // Letters settle in: tracking closes from wide to the resting spacing over the whole shot.
      const k = Math.min(1, age / c.spec.dur);
      const base = c.spec.kind === 'logo' ? 0.32 : 0.16;
      if (c.t) c.t.style.letterSpacing = `${(base + 0.18 * (1 - ease(Math.min(1, age / 1800))) + 0.04 * (1 - k)).toFixed(3)}em`;
      c.rule.style.width = `${Math.round(ease(Math.min(1, (age - 200) / 1200)) * (c.spec.kind === 'logo' ? 520 : 340))}px`;
      if (c.spec.kind === 'lower') lower = Math.max(lower, a);
      return true;
    });
    shadeTarget = lower;
    shade.style.opacity = (shadeTarget * 0.9).toFixed(3);
    black.style.opacity = blackLevel.toFixed(3);
  }

  // ---------------------------------------------------------------- scripted presence
  let fakeWs: { onmessage: ((ev: { data: string }) => void) | null } | null = null;
  const RealWS = window.WebSocket;
  class CaptureSocket {
    static readonly CONNECTING = 0;
    static readonly OPEN = 1;
    static readonly CLOSING = 2;
    static readonly CLOSED = 3;
    readyState = 1;
    onopen: (() => void) | null = null;
    onmessage: ((ev: { data: string }) => void) | null = null;
    onclose: ((ev: { code: number }) => void) | null = null;
    onerror: (() => void) | null = null;
    constructor() {
      fakeWs = this;
      queueMicrotask(() => this.onopen?.());
    }
    send(): void {}
    close(): void {
      this.readyState = 3;
    }
  }
  // Real presence (a local worker's FieldShard) re-stamped onto the virtual clock: each snapshot is
  // dated "now" in capture time, so players driven in lockstep with the frames move smoothly.
  let snapWaiters: ((rows: number) => void)[] = [];
  class RestampSocket {
    private readonly ws: WebSocket;
    onopen: (() => void) | null = null;
    onmessage: ((ev: { data: string }) => void) | null = null;
    onclose: ((ev: CloseEvent) => void) | null = null;
    onerror: (() => void) | null = null;
    constructor(url: string) {
      this.ws = new RealWS(url);
      this.ws.onopen = () => this.onopen?.();
      this.ws.onclose = (ev) => this.onclose?.(ev);
      this.ws.onerror = () => this.onerror?.();
      this.ws.onmessage = (ev) => {
        let data = String(ev.data);
        let rows = -1;
        try {
          const m = JSON.parse(data) as { t: string; ts?: number; s?: unknown[] };
          if (m.t === 'snap') {
            m.ts = vnow;
            rows = m.s?.length ?? 0;
            data = JSON.stringify(m);
          }
        } catch {
          /* not JSON: pass through */
        }
        this.onmessage?.({ data });
        if (rows >= 0) {
          const ws = snapWaiters;
          snapWaiters = [];
          for (const r of ws) r(rows);
        }
      };
    }
    get readyState(): number {
      return this.ws.readyState;
    }
    send(d: string): void {
      this.ws.send(d);
    }
    close(code?: number, reason?: string): void {
      this.ws.close(code, reason);
    }
  }
  w['WebSocket'] = function (this: unknown, url: string | URL, protocols?: string | string[]) {
    if (String(url).startsWith('ws://capture.local')) return new CaptureSocket();
    if (String(url).includes('/api/field/')) return new RestampSocket(String(url));
    return new RealWS(url, protocols);
  } as unknown as typeof WebSocket;
  Object.assign(w['WebSocket'] as object, { CONNECTING: 0, OPEN: 1, CLOSING: 2, CLOSED: 3, prototype: RealWS.prototype });

  const presence = {
    /** Show `players` (FieldPlayerInfo with their first state) as other heroes on this field. */
    start(players: FieldPlayerInfo[], heroName = 'Hero', classId = 'warrior'): void {
      startFieldPresence({ zoneId: 'mossvale', fieldId: 'mossvale', heroName, classId, hudHost: document.getElementById('hud')!, getLocalState: () => null, url: 'ws://capture.local/field' });
      queueMicrotask(() => fakeWs?.onmessage?.({ data: JSON.stringify({ t: 'welcome', you: 0, shard: 0, key: 'capture', players }) }));
    },
    /** One shard snapshot at the current virtual time. */
    snap(rows: FieldRow[], ev?: FieldEventOut[]): void {
      fakeWs?.onmessage?.({ data: JSON.stringify({ t: 'snap', ts: vnow, s: rows, ...(ev?.length ? { ev } : {}) }) });
    },
    /** Join a real presence shard at `url` (a token'd /api/field/… socket) as the local hero. */
    startReal(url: string, heroName = 'Hero', classId = 'warrior'): void {
      const ks = (window as unknown as { __keystone?: { session?: { sim: { getWorld(): never }; playerId: string | null } } }).__keystone;
      const sess = ks?.session;
      startFieldPresence({ zoneId: 'mossvale', fieldId: 'mossvale', heroName, classId, hudHost: document.getElementById('hud')!, url, getLocalState: sess?.playerId ? simLocalState(() => sess.sim.getWorld(), sess.playerId) : () => null });
    },
    /** Resolves with the row count of the next shard snapshot (0 on timeout, real ms). */
    nextSnap(timeoutMs = 1000): Promise<number> {
      return new Promise((r) => {
        snapWaiters.push(r);
        realSetTimeout(() => r(0), timeoutMs);
      });
    },
    stop(): void {
      stopFieldPresence();
      fakeWs = null;
    },
  };

  // ---------------------------------------------------------------- API
  const api = {
    get now(): number {
      return vnow;
    },
    get running(): boolean {
      return free;
    },
    freeze(): void {
      free = false;
    },
    resume(): void {
      lastReal = realNow();
      free = true;
    },
    step(dtMs = 1000 / 30, sub = 1): number {
      for (let i = 0; i < sub; i++) pump(dtMs / sub);
      return vnow;
    },
    setCamera(pos: CamSpec['pos'], look: CamSpec['look'], fov = 34, roll = 0): void {
      cam = { pos, look, fov, roll };
    },
    clearCamera(): void {
      cam = null;
    },
    /** Capture-only lighting preset ('golden', 'bright'), optionally tweaked; null = the theme's own. */
    light(name: string | null, overrides: Partial<Look> = {}): void {
      // +atmos: 'golden' (and dawn / day / dusk / night) is the time of day itself; the look only
      // keeps the capture's extra tweaks (less haze) on top.
      const tod = name !== null && (TOD_PRESETS as readonly string[]).includes(name) ? (name as TodPreset) : null;
      atmosphere.override = tod;
      if (tod) {
        const extra: Partial<Look> = tod === 'golden' ? { fogNear: 120, fogFar: 420 } : {};
        look = { ...extra, ...overrides };
        return;
      }
      look = name ? { ...LOOKS[name], ...overrides } : null;
    },
    /** Show / hide world labels (NPC names, quest and portal markers). */
    labels(on: boolean): void {
      labels = on;
    },
    /** 'all' hides the HUD, 'none' shows it; a list of selectors hides all but those (e.g. ['.floaters']). */
    hideHud(mode: 'all' | 'none' | boolean | string[] = 'all'): void {
      const keep = Array.isArray(mode) ? mode : null;
      const m = keep ? 'keep' : mode === true ? 'all' : mode === false ? 'none' : mode;
      const h = document.documentElement.classList;
      for (const k of ['all', 'keep']) h.toggle(`cap-hud-${k}`, m === k);
      keepStyle.textContent = keep?.length ? `${keep.flatMap((k) => [`html.cap-hud-keep :is(#hud, body) ${k}`, `html.cap-hud-keep :is(#hud, body) ${k} *`]).join(', ')} { visibility: visible !important; }` : '';
    },
    caption,
    clearCaptions(): void {
      for (const c of captions) c.el.remove();
      captions = [];
      tickOverlay();
    },
    /** 0..1 black over everything (dips between sections are done in the edit; this is for holds). */
    black(level: number): void {
      blackLevel = level;
      tickOverlay();
    },
    /** Field prop occlusion (the dither around the hero sight line): off for free cameras. */
    occlusion(on: boolean): void {
      OCC_U.uOccP.value.w = on ? 0.82 : 0;
    },
    presence,
  };
  (window as unknown as { __capture: unknown }).__capture = api;
  document.documentElement.classList.add('cap-nocursor');
  console.info('[capture] virtual clock installed');
}
